// src/pages/MyCases.jsx
//
// Dedicated page where users upload and review their own longitudinal MRI
// cases. Reached via /my-cases. Surface copy is intentionally clinical and
// non-technical — no model names, no dataset names, no inference jargon.
// The underlying API contract is unchanged.

import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiGet, apiDelete, fetchBlob, ApiError } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { usePolling } from '../hooks/usePolling';
import MriViewer from '../components/MriViewer';
import UploadCaseModal from '../components/UploadCaseModal';
import AddFollowupModal from '../components/AddFollowupModal';
import Navbar from '../components/Navbar';

import './MyCases.css';

const ALL_INTERVALS = [6, 12, 24];

// ---- helpers ----

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function statusBadge(status) {
  if (!status) return null;
  const map = {
    uploaded:      { label: 'Queued',          tone: 'pending' },
    preprocessing: { label: 'Processing scan', tone: 'pending' },
    ready:         { label: 'Baseline ready',  tone: 'ok'      },
    failed:        { label: 'Failed',          tone: 'fail'    },
    queued:        { label: 'Queued',          tone: 'pending' },
    inference:     { label: 'Assessing',       tone: 'pending' },
    done:          { label: 'Complete',        tone: 'ok'      },
  };
  const m = map[status] || { label: status, tone: 'pending' };
  return <span className={`badge badge--${m.tone}`}>{m.label}</span>;
}

function caseHasWork(c) {
  if (c.baseline_status === 'uploaded' || c.baseline_status === 'preprocessing') return true;
  return (c.followups || []).some(
    (f) => f.status === 'queued' || f.status === 'preprocessing' || f.status === 'inference'
  );
}

// ---- main page ----

export default function MyCases({ theme, toggleTheme }) {
  const { user } = useAuth();

  const fetcher = useCallback(() => apiGet('/api/cases', user), [user]);
  const shouldPoll = useCallback((data) => {
    if (!data?.cases) return true;
    return data.cases.some(caseHasWork);
  }, []);

  const { data, error, loading, refresh } = usePolling(fetcher, {
    intervalMs: 5000,
    shouldPoll,
  });

  const [showUpload, setShowUpload] = useState(false);
  const [addFollowupCaseId, setAddFollowupCaseId] = useState(null);
  const [expandedCaseId, setExpandedCaseId] = useState(null);

  const cases = data?.cases || [];
  const limits = data?.limits;
  const atQuota = limits && cases.length >= limits.max_cases_per_user;

  return (
    <>
      <Navbar theme={theme} toggleTheme={toggleTheme} />
      <div className="mycases">
        <div className="container mycases__container">
          <header className="mycases__header">
            <div>
              <span className="eyebrow">Your case library</span>
              <h1 className="mycases__title">My cases</h1>
              <p className="mycases__lead">
                Upload your own paired MRI scans — a baseline and one or more
                follow-ups — and receive a BrainAxis risk assessment for each
                pair. Every follow-up is assessed independently against the
                same baseline scan.
              </p>
            </div>
            <div className="mycases__actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowUpload(true)}
                disabled={atQuota}
                title={atQuota ? 'Case quota reached' : 'Upload a new case'}
              >
                Upload new case
              </button>
              {limits && (
                <span className="mycases__quota">
                  {cases.length} of {limits.max_cases_per_user} cases used
                </span>
              )}
            </div>
          </header>

          {error && (
            <div className="mycases__error" role="alert">
              Could not load your cases: {error.detail || error.message}
              <button className="btn btn-ghost btn-sm" onClick={refresh}>Retry</button>
            </div>
          )}

          {loading && !data && (
            <div className="mycases__loading">Loading…</div>
          )}

          {!loading && cases.length === 0 && (
            <div className="mycases__empty">
              <h2>No cases yet</h2>
              <p>
                Upload a baseline scan together with one follow-up to receive
                your first risk assessment. Additional follow-ups can be added
                later — one each at 6, 12, and 24 months after the baseline.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setShowUpload(true)}
                disabled={atQuota}
              >
                Upload your first case
              </button>
            </div>
          )}

          <div className="mycases__list">
            {cases.map((c, i) => (
              <CaseCard
                key={c.case_id}
                case_={c}
                caseNumber={i + 1}
                expanded={expandedCaseId === c.case_id}
                onToggle={() => setExpandedCaseId(
                  expandedCaseId === c.case_id ? null : c.case_id
                )}
                onAddFollowup={() => setAddFollowupCaseId(c.case_id)}
                onChanged={refresh}
                user={user}
              />
            ))}
          </div>
        </div>

        {showUpload && (
          <UploadCaseModal
            onClose={() => setShowUpload(false)}
            onCreated={() => {
              setShowUpload(false);
              refresh();
            }}
          />
        )}

        {addFollowupCaseId && (
          <AddFollowupModal
            caseId={addFollowupCaseId}
            usedIntervals={
              (cases.find((c) => c.case_id === addFollowupCaseId)?.followups || [])
                .map((f) => f.interval_months)
            }
            onClose={() => setAddFollowupCaseId(null)}
            onAdded={() => {
              setAddFollowupCaseId(null);
              refresh();
            }}
          />
        )}
      </div>
    </>
  );
}


// ---------------------------------------------------------------------------
// One case card
// ---------------------------------------------------------------------------

function CaseCard({ case_: c, caseNumber, expanded, onToggle, onAddFollowup, onChanged, user }) {
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const usedIntervals = (c.followups || []).map((f) => f.interval_months);
  const remainingIntervals = ALL_INTERVALS.filter((i) => !usedIntervals.includes(i));

  const title =
    c.subject_label?.trim() ||
    `Case ${caseNumber}`;

  async function handleDeleteCase() {
    if (!window.confirm(
      'Delete this case and all its follow-ups?\n\n' +
      'This permanently removes the uploaded scans and assessments.'
    )) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiDelete(`/api/cases/${encodeURIComponent(c.case_id)}`, user);
      onChanged?.();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.detail : 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  }

  async function handleDeleteFollowup(fu) {
    if (!window.confirm(
      `Delete the ${fu.interval_months}-month follow-up and its assessment?`
    )) return;
    try {
      await apiDelete(
        `/api/cases/${encodeURIComponent(c.case_id)}/followups/${encodeURIComponent(fu.followup_id)}`,
        user
      );
      onChanged?.();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.detail : 'Delete failed.');
    }
  }

  return (
    <article className={`case-card ${expanded ? 'case-card--expanded' : ''}`}>
      <header className="case-card__head">
        <div className="case-card__title-block">
          <h3 className="case-card__title">{title}</h3>
          <div className="case-card__meta">
            <span>Uploaded {fmtDate(c.created_at)}</span>
            {statusBadge(c.baseline_status)}
          </div>
        </div>
        <div className="case-card__actions">
          {c.baseline_status === 'ready' && remainingIntervals.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={onAddFollowup}>
              + Add follow-up
            </button>
          )}
          {c.baseline_has_preprocessed && (
            <button className="btn btn-ghost btn-sm" onClick={onToggle}>
              {expanded ? 'Hide scans' : 'View scans'}
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm btn-danger"
            onClick={handleDeleteCase}
            disabled={deleting}
            title="Delete case"
          >
            {deleting ? '…' : 'Delete'}
          </button>
        </div>
      </header>

      {c.baseline_status === 'failed' && c.baseline_error && (
        <div className="case-card__error">
          Could not process the baseline scan: {c.baseline_error}
        </div>
      )}

      {/* Follow-ups list */}
      <div className="case-card__followups">
        {(c.followups || []).length === 0 ? (
          <p className="case-card__no-fus">No follow-ups yet.</p>
        ) : (
          <table className="fu-table">
            <thead>
              <tr>
                <th>Follow-up</th>
                <th>Status</th>
                <th>Risk score</th>
                <th>Assessment</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {c.followups.map((fu) => (
                <tr key={fu.followup_id}>
                  <td>{fu.interval_months} months</td>
                  <td>{statusBadge(fu.status)}</td>
                  <td>
                    {fu.status === 'done' && fu.predicted_probability != null ? (
                      <span className="fu-prob">
                        {(fu.predicted_probability * 100).toFixed(1)}%
                      </span>
                    ) : fu.status === 'failed' && fu.error ? (
                      <span className="fu-err" title={fu.error}>—</span>
                    ) : '—'}
                  </td>
                  <td>
                    {fu.status === 'done' && fu.predicted_class ? (
                      <span className={`pred pred--${fu.predicted_class}`}>
                        {fu.predicted_class === 'converter'
                          ? 'Likely to progress'
                          : 'Likely to remain stable'}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <button
                      className="btn btn-ghost btn-sm btn-danger"
                      onClick={() => handleDeleteFollowup(fu)}
                      title="Delete this follow-up"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Trajectory: simple inline summary when 2+ done */}
        <Trajectory followups={c.followups || []} />
      </div>

      {deleteError && (
        <div className="case-card__error">{deleteError}</div>
      )}

      {/* Expanded MRI viewer */}
      {expanded && c.baseline_has_preprocessed && (
        <ExpandedScanViewer case_={c} user={user} />
      )}
    </article>
  );
}


// ---------------------------------------------------------------------------
// Trajectory: shows the risk score series across completed follow-ups
// ---------------------------------------------------------------------------

function Trajectory({ followups }) {
  const done = followups
    .filter((f) => f.status === 'done' && f.predicted_probability != null)
    .sort((a, b) => a.interval_months - b.interval_months);

  if (done.length < 2) return null;

  return (
    <div className="trajectory">
      <span className="trajectory__label">Risk over time:</span>
      {done.map((f, i) => (
        <React.Fragment key={f.followup_id}>
          {i > 0 && <span className="trajectory__arrow">→</span>}
          <span className="trajectory__point">
            <span className="trajectory__interval">{f.interval_months}mo</span>
            <strong>{(f.predicted_probability * 100).toFixed(0)}%</strong>
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Expanded view: MRI viewer for baseline + one selected follow-up
// ---------------------------------------------------------------------------

function ExpandedScanViewer({ case_: c, user }) {
  const readyFus = (c.followups || []).filter(
    (f) => f.has_preprocessed && (f.status === 'done' || f.status === 'inference')
  );
  const [selectedFuId, setSelectedFuId] = useState(readyFus[0]?.followup_id || null);
  const [blUrl, setBlUrl] = useState(null);
  const [fuUrl, setFuUrl] = useState(null);
  const [viewerError, setViewerError] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    let baselineObjectUrl = null;
    let followupObjectUrl = null;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setViewerError(null);
      try {
        baselineObjectUrl = await fetchBlob(
          `/api/cases/${encodeURIComponent(c.case_id)}/scan/baseline`, user
        );
        if (cancelled) return;
        if (!cancelled) setBlUrl(baselineObjectUrl);

        if (selectedFuId) {
          followupObjectUrl = await fetchBlob(
            `/api/cases/${encodeURIComponent(c.case_id)}` +
            `/followups/${encodeURIComponent(selectedFuId)}/scan`,
            user
          );
          if (cancelled) return;
          setFuUrl(followupObjectUrl);
        } else {
          setFuUrl(null);
        }
      } catch (err) {
        if (!cancelled) setViewerError(
          err instanceof ApiError ? err.detail : 'Could not load the scans.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();

    return () => {
      cancelled = true;
      if (baselineObjectUrl) URL.revokeObjectURL(baselineObjectUrl);
      if (followupObjectUrl) URL.revokeObjectURL(followupObjectUrl);
    };
  }, [c.case_id, selectedFuId, user]);

  return (
    <div className="case-viewer">
      {readyFus.length > 1 && (
        <div className="case-viewer__select">
          <label htmlFor={`fu-sel-${c.case_id}`}>Follow-up:</label>
          <select
            id={`fu-sel-${c.case_id}`}
            value={selectedFuId || ''}
            onChange={(e) => setSelectedFuId(e.target.value || null)}
          >
            {readyFus.map((f) => (
              <option key={f.followup_id} value={f.followup_id}>
                {f.interval_months} months
              </option>
            ))}
          </select>
        </div>
      )}

      {loading && <p className="case-viewer__loading">Loading scans…</p>}
      {viewerError && <p className="case-card__error">{viewerError}</p>}
      {!loading && !viewerError && blUrl && (
        <MriViewer baselineUrl={blUrl} followupUrl={fuUrl} />
      )}
    </div>
  );
}
